import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { ControlledDto, syncInstallation } from 'src/Libs/flow/installation/api'
import { getEebusPUTDto, getModbusTcpPUTDto, parseEebusChargingStation, parseModbusTcpChargingStation } from 'src/Libs/flow/parsers'
import { MIN_EVSE_HOLDING_TIME_IN_MINUTES, MAX_EVSE_HOLDING_TIME_IN_MINUTES } from 'src/Libs/flow/constants'
import { ModbusTcpChargingStation, EebusChargingStation } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { BaseNumberField as NumberField } from 'src/Libs/inputs/react-hook-form/NumberField'
import { makeStyles } from 'tss-react/mui'
import { ControlledModbusTcpPUTDto, updateEvse, ControlledEebusPUTDto } from '../../api/evse.api.js'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import useAppRatingContext from 'src/Libs/app-rating/hooks/use-app-rating-context.js'
import { usePortalMutation } from 'src/Libs/api'

const INTEGER_REGEX = new RegExp(/^\d*$/)

const useStyles = makeStyles()(theme => ({
  dialogContent: {
    [theme.breakpoints.down('md')]: {
      marginBottom: 0,
    },
  },
}))

export interface HoldingTimeEditorDialogProps {
  installationId: number
  queryIsLoading: boolean
  evse: Nullable<ControlledDto>
  onClose?: () => void
}

/** Dialog to change the an EMC's holding time (minutes). */
export const HoldingTimeEditorDialog: React.FC<HoldingTimeEditorDialogProps> = ({ onClose, installationId, queryIsLoading, evse }) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()

  const { classes } = useStyles()

  const { enqueueErrorStack } = useSnackbar()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const parsedEvse = evse.media === 'ModbusTCP' ? parseModbusTcpChargingStation(evse) : parseEebusChargingStation(evse)

  const currentHoldingTime = parsedEvse.holdingTime.toString()
  const [holdingTime, setHoldingTime] = useLatestState(currentHoldingTime)

  const holdingTimeNumber = Number.parseInt(holdingTime.toString()) || 0
  const hasError = MIN_EVSE_HOLDING_TIME_IN_MINUTES > holdingTimeNumber || holdingTimeNumber > MAX_EVSE_HOLDING_TIME_IN_MINUTES

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: ControlledModbusTcpPUTDto | ControlledEebusPUTDto) =>
      updateEvse(installationId, evse.deviceLinkId?.toString() ?? '', evse.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      })
      onClose?.()
    },
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setHoldingTime(currentHoldingTime)
    },
  })

  const isHoldingTimeSameAsInitial = holdingTime === currentHoldingTime

  const handleSave = (): void => {
    if (isHoldingTimeSameAsInitial) return

    mutateEvse.mutate(
      evse.media === 'ModbusTCP'
        ? getModbusTcpPUTDto({
            ...(parsedEvse as ModbusTcpChargingStation),
            holdingTime: holdingTimeNumber,
          })
        : getEebusPUTDto({
            ...(parsedEvse as EebusChargingStation),
            holdingTime: holdingTimeNumber,
          })
    )
    triggerAppRating()
  }
  const dialogActions: DialogActions = {
    primary: {
      name: t('common.save'),
      onClick: handleSave,
      disabled: isHoldingTimeSameAsInitial || hasError || !userCanEdit || mutateEvse.isPending,
      isLoading: queryIsLoading || mutateEvse.isPending,
    },
    secondary: {
      name: t('common.cancel'),
      onClick: onClose,
    },
  }

  return (
    <Dialog
      title={t('user-settings.evse-advanced-page.holding-time.title')}
      maxWidth="md"
      actions={dialogActions}
      contentClassName={classes.dialogContent}
      onClose={onClose}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'center', sm: 'baseline' }} gap={2} p={0}>
        <Typography variant="body1" sx={{ color: 'palette.grey[800]' }}>
          {t('user-settings.evse-advanced-page.holding-time.information')}
        </Typography>
        <Stack minWidth={{ xs: '100%', sm: 'auto' }}>
          <NumberField
            value={holdingTime}
            inputMode="numeric"
            onChange={e => {
              if (INTEGER_REGEX.test(e.target.value)) {
                setHoldingTime(e.target.value)
              }
            }}
            suffix={t('affixes.minute')}
          />
          <Typography variant="caption" color={'error'} textAlign={'center'} display={'block'} mt={1} visibility={hasError ? undefined : 'hidden'}>
            {t('user-settings.evse-advanced-page.holding-time.range-error', {
              min: MIN_EVSE_HOLDING_TIME_IN_MINUTES,
              max: MAX_EVSE_HOLDING_TIME_IN_MINUTES,
            })}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  )
}
